import React, { useRef, forwardRef, useState, useEffect, useCallback } from 'react'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import { Portal } from 'react-portal'
import Input from '../Input'
import useRootClose from '../hooks/useRootClose'
import { usePopper } from 'react-popper'
import useMergedRef from '../hooks/useMergeRef'
import { HiOutlineCalendar } from 'react-icons/hi'
import CloseButton from '../CloseButton'

dayjs.extend(localizedFormat)

const BasePicker = forwardRef((props, ref) => {
    const {
        className,
        clearButton,
        children,
        form,
        field,
        size,
        inputtable,
        inputtableBlurClose = true,
        inputLabel,
        inputPrefix,
        inputSuffix = <HiOutlineCalendar className="text-lg" />,
        clearable = true,
        placeholder,
        name,
        dropdownOpened,
        setDropdownOpened,
        onDropdownOpen,
        onDropdownClose,
        onBlur,
        onFocus,
        onChange,
        onKeyDown,
        onClear,
        disabled,
        type,
        placement="bottom-start",
    } = props

    const handleInputClick = () => {
        !inputtable ? toggleDropdown() : openDropdown()
    }

    const closeDropdown = () => {
        setDropdownOpened(false)
        onDropdownClose?.()
    }

    const suffixIconSlot = clearable ? (
        <div onClick={onClear}>
            {clearButton || <CloseButton className="text-base" />}
        </div>
    ) : (
        <>{inputSuffix}</>
    )

    const toggleDropdown = () => {
        setDropdownOpened(!dropdownOpened)
        !dropdownOpened ? onDropdownOpen?.() : onDropdownClose?.()
    }

    const openDropdown = () => {
        setDropdownOpened(true)
        onDropdownOpen?.()
    }

    const handleKeyDown = (event) => {
        typeof onKeyDown === 'function' && onKeyDown(event)
        if ((event.key === 'Space' || event.key === 'Enter') && !inputtable) {
            event.preventDefault()
            openDropdown()
        }
    }

    const handleInputBlur = (event) => {
        typeof onBlur === 'function' && onBlur(event)
        if (inputtable && inputtableBlurClose) {
            closeDropdown()
        }
    }

    const handleInputFocus = (event) => {
        typeof onFocus === 'function' && onFocus(event)
        if (inputtable) {
            openDropdown()
        }
    }

    const referenceRef = useRef(null)
    const popperRef = useRef(null)
    const [referenceElement, setReferenceElement] = useState(null)
    const [popperElement, setPopperElement] = useState(null)

    const setReferenceRef = useCallback((node) => {
        referenceRef.current = node
        setReferenceElement(node)
    }, [])

    const setPopperRef = useCallback((node) => {
        popperRef.current = node
        setPopperElement(node)
    }, [])

    const { styles, attributes, update } = usePopper(
        referenceElement,
        popperElement,
        {
            placement,
            strategy: 'fixed',
            modifiers: [
                {
                    name: 'offset',
                    options: { offset: [0, 8] },
                },
                {
                    name: 'preventOverflow',
                    options: { padding: 8 },
                },
                {
                    name: 'flip',
                    options: { fallbackPlacements: ['bottom-end', 'top-start', 'top-end'] },
                },
            ],
        }
    )

    useEffect(() => {
        if (dropdownOpened && update) {
            update()
        }
    }, [dropdownOpened, update])

    useRootClose(() => closeDropdown(), {
        triggerTarget: referenceRef,
        overlayTarget: popperRef,
        disabled: !dropdownOpened,
        listenEscape: false,
    })

    const pickerDropdown = dropdownOpened ? (
        <div
            className="picker"
            ref={setPopperRef}
            style={styles.popper}
            {...attributes.popper}
        >
            <div className="picker-panel">{children}</div>
        </div>
    ) : null

    return (
        <>
            <Input
                form={form}
                field={field}
                className={className}
                placeholder={placeholder}
                ref={useMergedRef(ref, setReferenceRef)}
                size={size}
                name={name}
                value={inputLabel}
                readOnly={!inputtable}
                suffix={suffixIconSlot}
                prefix={inputPrefix}
                onClick={handleInputClick}
                onKeyDown={handleKeyDown}
                onBlur={handleInputBlur}
                onFocus={handleInputFocus}
                onChange={onChange}
                autoComplete="off"
                type={type}
                disabled={disabled}
            />
            {typeof document !== 'undefined' ? (
                <Portal>{pickerDropdown}</Portal>
            ) : (
                pickerDropdown
            )}
        </>
    )
})

export default BasePicker
